/**
 * Repère de position pour les listes de chat.
 *
 * Le chat du live rejoue lui-même le calage de la vue, absent de Safari. Le repère ne peut pas
 * être un message unique : il peut quitter la liste rendue (épinglé, donc déplacé hors du
 * conteneur, modéré, filtré, ou évincé par le plafond de 300). On garde donc une file de
 * candidats et on retient le premier encore affiché.
 *
 * La correction est **relative** : on mémorise la position du message dans la zone visible, et on
 * ne fait que rattraper l'écart constaté. Une position absolue reconstituée à partir de valeurs
 * mémorisées pouvait produire un saut arbitraire quand l'une d'elles n'était plus cohérente, et
 * dans un conteneur en colonne inversée « n'importe où » veut dire le bas de liste.
 */

export type ScrollAnchorCandidate = {
  id: string;
  /** Distance entre le haut du message et le haut de la zone visible, au moment de la capture. */
  viewportTop: number;
};

/**
 * Premier candidat encore présent dans la liste rendue, ou `null` si aucun n'a survécu. Dans ce
 * dernier cas l'appelant ne recale rien : mieux vaut laisser défiler que sauter au hasard.
 */
export function selectSurvivingAnchor(
  candidates: ScrollAnchorCandidate[] | null | undefined,
  isPresent: (id: string) => boolean,
): ScrollAnchorCandidate | null {
  if (!candidates) return null;
  for (const candidate of candidates) {
    if (candidate.id && isPresent(candidate.id)) return candidate;
  }
  return null;
}

/**
 * Correction à appliquer au défilement pour ramener le message à la place qu'il occupait.
 * Renvoie `0` quand il n'y a rien à faire, et quand l'écart est incohérent : au-delà de plusieurs
 * hauteurs de panneau, le repère est périmé et ne rien faire vaut mieux que sauter.
 */
export function resolveScrollCorrection(
  currentViewportTop: number,
  anchor: ScrollAnchorCandidate,
  viewportHeight: number,
): number {
  const delta = currentViewportTop - anchor.viewportTop;
  if (!Number.isFinite(delta) || delta === 0) return 0;
  if (viewportHeight > 0 && Math.abs(delta) > viewportHeight * ANCHOR_MAX_CORRECTION_RATIO) {
    return 0;
  }
  return delta;
}

/** Au-delà de cet écart, exprimé en hauteurs de panneau, on considère le repère périmé. */
export const ANCHOR_MAX_CORRECTION_RATIO = 10;

/**
 * Le lecteur suit-il le direct ? Les listes de chat sont en colonne inversée : le bas, c'est
 * `scrollTop` à zéro, et remonter donne une valeur négative sur certains navigateurs.
 */
export function isFollowingBottom(scrollTop: number, thresholdPx: number): boolean {
  return Math.abs(scrollTop) <= thresholdPx;
}
